import React from 'react';
import styled from 'styled-components';
import { Grid, Col } from '@enso/components';
import { utils } from '@enso/components';
import theme from '../../../theme';

const SectionContainer = styled.section<{
  noMarginTop?: boolean;
  backgroundColor?: string;
}>`
  padding: 0px 24px 60px;
  background-color: ${props =>
    props.backgroundColor || props.theme.color.backgroundWhite};
  margin-top: ${props => (props.noMarginTop ? '0px' : '-90px')};

  @media ${utils.device.laptop} {
    padding: 0px 140px 100px;
    margin-top: ${props => (props.noMarginTop ? '0px' : '-120px')};
  }
`;

const Title = styled.h2`
  font-family: ${props => props.theme.font.title};
  font-size: 28px;
  font-weight: bold;
  color: #140d38;
  margin-bottom: 16px;
  line-height: 1.3;

  @media ${utils.device.laptop} {
    font-size: 40px;
    margin-bottom: 24px;
    line-height: 1.2;
  }
`;

const Description = styled.p`
  font-family: ${props => props.theme.font.body};
  font-size: 16px;
  color: #140d38;
  margin-bottom: 32px;
  line-height: 1.6;

  @media ${utils.device.laptop} {
    font-size: 18px;
    margin-bottom: 40px;
  }
`;

const CTAButton = styled.span`
  display: inline-block;
  padding: 0;
  background-color: transparent;
  color: #fd5e43;
  font-family: ${props => props.theme.font.body};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s;
  letter-spacing: 0.5px;
  cursor: default;
`;

const ImageContainer = styled.div<{ reverse?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;

  @media ${utils.device.laptop} {
    margin-top: 0;
    justify-content: ${props => (props.reverse ? 'flex-end' : 'flex-start')};
  }
`;

const Image = styled.img`
  max-width: 100%;
  height: auto;
  width: 100%;

  @media ${utils.device.laptop} {
    max-width: 500px;
    width: auto;
  }
`;

SectionContainer.defaultProps = { theme };
Title.defaultProps = { theme };
Description.defaultProps = { theme };
CTAButton.defaultProps = { theme };

type SectionProps = {
  title: string | React.ReactNode;
  description: string;
  ctaText: string;
  ctaLink: string;
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean;
  backgroundColor?: string;
  noMarginTop?: boolean;
};

const Section = function({
  title,
  description,
  ctaText,
  ctaLink,
  imageSrc,
  imageAlt,
  reverse = false,
  backgroundColor,
  noMarginTop = false,
}: SectionProps) {
  return (
    <SectionContainer
      backgroundColor={backgroundColor}
      noMarginTop={noMarginTop}
    >
      <Grid xs="column" md="row">
        {reverse ? (
          <>
            <Col md="6">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Title>{title}</Title>
                <Description>{description}</Description>
                <CTAButton>{ctaText}</CTAButton>
              </div>
            </Col>
            <Col md="6">
              <ImageContainer reverse={reverse}>
                <Image src={imageSrc} alt={imageAlt} />
              </ImageContainer>
            </Col>
          </>
        ) : (
          <>
            <Col md="6">
              <ImageContainer reverse={reverse}>
                <Image src={imageSrc} alt={imageAlt} />
              </ImageContainer>
            </Col>
            <Col md="6">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Title>{title}</Title>
                <Description>{description}</Description>
                <CTAButton>{ctaText}</CTAButton>
              </div>
            </Col>
          </>
        )}
      </Grid>
    </SectionContainer>
  );
};

export default Section;
